import * as React from 'react';
import {
  BriefcaseIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  CodeBracketSquareIcon,
  UserCircleIcon,
} from '@heroicons/react/20/solid';
import { Dropdown } from '@nextui-org/react';
import { produce } from 'immer';
import { useRouter } from 'next/router';
import { ROUTES } from '@src/config/constants';
import { AppFilterableListItem } from '@src/layouts/app/AppFilterableList';
import { ModalClientSelect } from '@src/layouts/feed/ModalClientSelect';
import { ModalHandleSelect } from '@src/layouts/feed/ModalHandleSelect';
import { ModalStatusSelect } from '@src/layouts/feed/ModalStatusSelect';
import { ModalTagSelect } from '@src/layouts/feed/ModalTagSelect';
import { decodeQueryParams, encodeQueryParams } from '@src/util/navigation';
import { DateRangeSelect, ModalDateRangeSelect } from './ModalDateRangeSelect';

interface AddNewProps {}

/**
 * @name AddNew
 * @description tbd...
 */
export function AddNew(_props: AddNewProps) {
  // const {} = props

  // Hooks
  const router = useRouter();
  const [isClientSelectOpen, setIsClientSelectOpen] = React.useState(false);
  const [isTagSelectOpen, setIsTagSelectOpen] = React.useState(false);
  const [isHandleSelectOpen, setIsHandleSelectOpen] = React.useState(false);
  const [isStatusSelectOpen, setIsStatusSelectOpen] = React.useState(false);
  const [isDateRangeSelectOpen, setIsDateRangeSelectOpen] =
    React.useState(false);

  // Setup
  const query = decodeQueryParams(router.query);
  const selectedClientNames = query.clients || [];
  const selectedTagNames = query.tags || [];
  const selectedHandles = query.handles || [];
  const selectedStatuses = query.statuses || [];
  const selectedStartDate = query.startDate
    ? new Date(query.startDate)
    : new Date();
  const selectedEndDate = query.endDate ? new Date(query.endDate) : new Date();

  // Handlers
  const onAction = (action: React.Key) => {
    switch (action) {
      case 'client':
        setIsClientSelectOpen(true);
        break;

      case 'tag':
        setIsTagSelectOpen(true);
        break;

      case 'date':
        setIsDateRangeSelectOpen(true);
        break;

      case 'handle':
        setIsHandleSelectOpen(true);
        break;

      case 'status':
        setIsStatusSelectOpen(true);
        break;

      default:
        break;
    }
  };

  const onClientSelect = (clients?: AppFilterableListItem[]) => {
    if (clients) {
      const newQuery = produce(query, (draft) => {
        draft.clients = clients.map((c) => c.name);
      });

      router.push({
        pathname: ROUTES.feed,
        query: encodeQueryParams(newQuery),
      });
    }

    setIsClientSelectOpen(false);
  };

  const onTagSelect = (tags?: AppFilterableListItem[]) => {
    if (tags) {
      const newQuery = produce(query, (draft) => {
        draft.tags = tags.map((t) => t.name);
      });

      router.push({
        pathname: ROUTES.feed,
        query: encodeQueryParams(newQuery),
      });
    }

    setIsTagSelectOpen(false);
  };

  const onHandleSelect = (handles?: AppFilterableListItem[]) => {
    if (handles) {
      const newQuery = produce(query, (draft) => {
        draft.handles = handles.map((t) => t.name);
      });

      router.push({
        pathname: ROUTES.feed,
        query: encodeQueryParams(newQuery),
      });
    }

    setIsHandleSelectOpen(false);
  };

  const onStatusSelect = (statuses?: AppFilterableListItem[]) => {
    if (statuses) {
      const newQuery = produce(query, (draft) => {
        draft.statuses = statuses.map((t) => t.name);
      });

      router.push({
        pathname: ROUTES.feed,
        query: encodeQueryParams(newQuery),
      });
    }

    setIsStatusSelectOpen(false);
  };

  const onDateRangeSelect = (dateRange: DateRangeSelect) => {
    const { endDate, startDate } = dateRange;

    const newQuery = produce(query, (draft) => {
      draft.startDate = startDate;
      draft.endDate = endDate;
    });

    router.push({
      pathname: ROUTES.feed,
      query: encodeQueryParams(newQuery),
    });

    setIsDateRangeSelectOpen(false);
  };

  return (
    <>
      <Dropdown>
        <Dropdown.Button
          light
          animated={false}
          ripple={false}
          css={{ zIndex: 0 }}
        >
          Add Filter
        </Dropdown.Button>
        <Dropdown.Menu aria-label="Static Actions" onAction={onAction}>
          <Dropdown.Item
            key="tag"
            color="primary"
            description='Add a new "Tag" filter'
            icon={<BriefcaseIcon width={20} height={20} />}
          >
            Tag
          </Dropdown.Item>
          <Dropdown.Item
            key="client"
            color="error"
            description='Add a new "Campaign" filter'
            icon={<BuildingOfficeIcon width={20} height={20} />}
          >
            Campaign
          </Dropdown.Item>
          <Dropdown.Item
            key="handle"
            color="secondary"
            description='Add a new "Handle" filter'
            icon={<UserCircleIcon width={20} height={20} />}
          >
            Handle
          </Dropdown.Item>
          <Dropdown.Item
            key="date"
            color="warning"
            description='Add a new "Date Range" filter'
            icon={<CalendarDaysIcon width={20} height={20} />}
          >
            Date
          </Dropdown.Item>
          <Dropdown.Item
            key="status"
            color="success"
            description='Add a new "Status" filter'
            icon={<CodeBracketSquareIcon width={20} height={20} />}
          >
            Status
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      {/* Modals */}
      <ModalClientSelect
        visible={isClientSelectOpen}
        onOk={onClientSelect}
        onCancel={() => setIsClientSelectOpen(false)}
        selected={selectedClientNames}
      />

      <ModalTagSelect
        visible={isTagSelectOpen}
        onOk={onTagSelect}
        onCancel={() => setIsTagSelectOpen(false)}
        selected={selectedTagNames}
      />
      <ModalHandleSelect
        visible={isHandleSelectOpen}
        onOk={onHandleSelect}
        onCancel={() => setIsHandleSelectOpen(false)}
        selected={selectedHandles}
      />
      <ModalStatusSelect
        visible={isStatusSelectOpen}
        onOk={onStatusSelect}
        onCancel={() => setIsStatusSelectOpen(false)}
        selected={selectedStatuses}
      />
      <ModalDateRangeSelect
        visible={isDateRangeSelectOpen}
        onOk={onDateRangeSelect}
        onCancel={() => setIsDateRangeSelectOpen(false)}
        selected={{ endDate: selectedEndDate, startDate: selectedStartDate }}
      />
    </>
  );
}
